/**
 * Single source of truth for the Registrations table's column pool.
 *
 * `useRegistrationTableColumns` resolves the full, config-driven column pool
 * (config-injected `configuredRegistrationColumns` OR `fallbackColumns`, plus
 * template/attribute-derived columns) - identical to what the Registrations
 * list page rendered when this logic lived inline. Both the Registrations
 * list page and the dashboard "Recent registrations" worklist tile consume
 * this hook so column definitions never drift between the two surfaces.
 */

import { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useProductTemplates } from '@/admin/hooks/use-products'
import { useTransactionTemplates } from '@/admin/hooks/use-transaction-templates'
import { useWarrantyTemplates } from '@/admin/hooks/use-warranties'
import {
  useEntityTypeByKey,
  useEntityTypes,
  type EntityAttributeDefinition,
  type EntityTableDisplayColumn,
  type EntityTypeDefinition,
} from '@/admin/hooks/use-entities'
import { useExternalRoles } from '@/admin/hooks/use-external-roles'
import { capitalizeFirstLetter } from '@/shared/lib/utils'
import {
  useRegistrationSettings,
  hostNounForDetailView,
  isHostBasedDetailView,
} from '@/admin/hooks/use-registration-settings'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { WorkflowStateBadge } from '@/shared/components/workflows'
import { AlertTriangle } from 'lucide-react'
import { type DataTableColumn } from '@/admin/components/data-table'
import { useTranslation } from 'react-i18next'
import {
  resolveRegistrationDisplayColumnValue,
  isWorkflowStateResolution,
} from '@/admin/lib/entity-card-display'
import { formatHoldingId } from '@/admin/lib/formatting'
import { useFormatters } from '@/shared/lib/formatting'
import { defineField, type FieldType } from '@/shared/lib/forms'
import type { Holding } from '@/admin/types/products'

interface HoldingAttributeValue {
  code_name: string
  display_name: string
  field_type?: string
  value: unknown
}

export interface WarrantySummary {
  display_name: string
  status: string
  expires_at: string | null
  active_from: string
}

export interface HoldingWarranty {
  class_name?: string
  option_display_name?: string
  warranty_type?: string
  status?: string
  active_from?: string
  expires_at?: string | null
  duration_in_months?: number | null
  attribute_values?: HoldingAttributeValue[]
}

interface TransactionAttributeValue {
  code_name: string
  display_name: string
  field_type?: string
  value: unknown
}

export interface HostSummary {
  uuid?: string
  kind?: string
  name?: string
  registration_number?: string
  vin?: string
  /** Canonical `{display, value}` envelope built by the API's `format_address`. */
  address?: { display?: string; value?: Record<string, unknown> } | null
  address_line_1?: string
  address_line_2?: string
  address_town_or_city?: string
  address_county?: string
  address_postcode?: string
  address_country?: string
  attribute_values?: HoldingAttributeValue[]
}

export interface WorkflowStateRef {
  instance_uuid: string
  workflow_key: string
  workflow_label: string
  owns_resource_lifecycle?: boolean
  state_key: string
  state_label: string
  state_color: string
  is_terminal: boolean
  attribute_values?: Record<string, unknown>
  attribute_display_values?: Record<string, string>
}

// Platform default key for the holding lifecycle status workflow. The
// actual key is read per-tenant off the ``holding.status`` attribute's
// ``configuration_data.workflow_key`` (see ``getConfiguredStatusWorkflowKey``);
// this is only the fallback when a tenant hasn't configured one.
const DEFAULT_STATUS_WORKFLOW_KEY = 'lifecycle_status'

function pickWorkflowState(
  states: WorkflowStateRef[] | undefined,
  workflowKey: string,
): WorkflowStateRef | null {
  if (!states?.length) return null
  return states.find((s) => s.workflow_key === workflowKey) ?? null
}

function getAttributeValue(row: HoldingRow, codeName: string) {
  const attrValue = row.attribute_values?.find((attr) => attr.code_name === codeName)?.value
  if (attrValue !== null && attrValue !== undefined && attrValue !== '') {
    return String(attrValue)
  }
  const registrationValue = row.registration_field_values?.[codeName]
  if (
    registrationValue !== null &&
    registrationValue !== undefined &&
    registrationValue !== ''
  ) {
    return String(registrationValue)
  }
  return ''
}

/**
 * The host's address, expressed as a display column so it resolves through
 * exactly the same machinery a tenant-authored `host.address` column would.
 * Host-based tenants (property, vehicle) identify a registration by where
 * it is, so this rides alongside the Reg # instead of waiting to be toggled
 * on in the column picker.
 */
export const HOST_ADDRESS_COLUMN_ID = 'holding:host:address'

const HOST_ADDRESS_DISPLAY_COLUMN: EntityTableDisplayColumn = {
  id: HOST_ADDRESS_COLUMN_ID,
  entity_key: 'host',
  attribute_code_name: 'address',
}

/**
 * Whether the tenant's host records an address at all. A vehicle host is
 * identified by plate and VIN and registers no address, so the column would
 * be present and permanently empty for them.
 */
export function hostCarriesAddress(
  hostEntity: EntityTypeDefinition | undefined,
): hostEntity is EntityTypeDefinition {
  return Boolean(hostEntity?.attributes?.some((a) => a.code_name === 'address'))
}

function resolveRegistrationColumnValue(
  row: HoldingRow,
  column: EntityTableDisplayColumn,
  statusWorkflowKey: string,
  entityTypes: EntityTypeDefinition[],
) {
  return resolveRegistrationDisplayColumnValue(row as unknown as Holding, column, {
    statusWorkflowKey,
    entityTypes,
  })
}

/**
 * Walk the configured column's relationship_path through the entity
 * registry to determine the terminal entity key. The terminal entity is
 * where ``attribute_code_name`` lives, so callers use it to look up the
 * attribute's ``field_type`` for display formatting.
 *
 * Mirrors the initial-context logic in
 * ``resolveRegistrationDisplayColumnValue``: paths that start with
 * ``holding``/``holdings`` are treated as starting from the host entity
 * (the typical layout for cards configured ON the host); everything
 * else starts from the holding.
 */
function resolveTerminalEntityKey(
  column: EntityTableDisplayColumn,
  entityTypes: EntityTypeDefinition[],
): string {
  const path = column.relationship_path?.length
    ? column.relationship_path
    : column.relationship_key
      ? [column.relationship_key]
      : []
  let currentKey =
    path.length === 0
      ? column.entity_key || 'holding'
      : path[0] === 'holdings' || path[0] === 'holding'
        ? 'host'
        : 'holding'
  for (const key of path) {
    const ent = entityTypes.find((e) => e.key === currentKey)
    const rel = ent?.relationships.find((r) => r.key === key)
    if (rel?.target_entity_key) {
      currentKey = rel.target_entity_key
    } else {
      // Fallbacks for relationships not registered in the entity
      // registry (e.g. partnership/organisation aliasing) - assume the
      // relationship key matches the target entity key.
      currentKey =
        key === 'holdings' ? 'holding' : key === 'warranties' ? 'warranty' : key
    }
  }
  return currentKey
}

function getRegistrationNumber(row: HoldingRow) {
  return (
    row.registration_number ||
    getAttributeValue(row, 'registration_number') ||
    formatHoldingId(row.uuid)
  )
}

function entityDisplayAttributes(
  attributes: EntityAttributeDefinition[] | undefined,
  surface: 'table' | 'detail',
) {
  return (attributes ?? []).filter((attribute) => {
    const config = attribute.configuration_data ?? {}
    const isWorkflowState =
      config.value_source === 'workflow_state' &&
      Boolean(config.workflow_key || attribute.code_name)
    const isRelationship =
      config.value_source === 'entity_relationship' &&
      Boolean(config.relationship_key || attribute.code_name)
    return (
      (isWorkflowState || isRelationship) &&
      Boolean(config.display?.[surface])
    )
  })
}

function formatRelationshipValue(row: HoldingRow, relationshipKey: string) {
  switch (relationshipKey) {
    case 'consumer': {
      const consumer = row.consumer_account
      const name = [consumer?.first_name, consumer?.last_name].filter(Boolean).join(' ')
      return name || consumer?.email || ''
    }
    case 'organisation':
      return row.partnership?.downstream_organisation_name || row.dealer_name || ''
    case 'product':
      return row.product?.cached_short_name || row.product?.cached_long_name || ''
    case 'warranty':
    case 'warranties':
    case 'warranty_option':
      return row.warranty_summary?.display_name || row.warranty_summary?.status || ''
    default:
      return ''
  }
}

function getConfiguredStatusWorkflowKey(
  attributes: EntityAttributeDefinition[] | undefined,
) {
  const statusAttribute = (attributes ?? []).find((attr) => attr.code_name === 'status')
  if (
    statusAttribute?.configuration_data?.value_source === 'workflow_state' &&
    statusAttribute.configuration_data.workflow_key
  ) {
    return statusAttribute.configuration_data.workflow_key
  }
  return DEFAULT_STATUS_WORKFLOW_KEY
}

export interface HoldingRow {
  uuid: string
  registration_number?: string
  product?: {
    uuid?: string
    cached_short_name?: string
    cached_long_name?: string
    product_type?: string
    product_template?: {
      name?: string
    } | null
    category?: {
      name?: string
    } | null
    parent_product?: {
      uuid?: string
      cached_short_name?: string
      cached_long_name?: string
      product_type?: string
    } | null
    attribute_values?: HoldingAttributeValue[]
  } | null
  consumer_account?: {
    uuid?: string
    first_name?: string
    last_name?: string
    email?: string
    phone_number?: string
    attribute_values?: HoldingAttributeValue[]
  } | null
  dealer_name?: string
  consumer_phone?: string
  registered_by_email?: string
  partnership?: {
    uuid?: string
    downstream_organisation_name?: string
    membership_number?: string
  } | null
  host?: HostSummary | null
  structure?: {
    uuid?: string
    name?: string
    structure_type?: string
    attribute_values?: HoldingAttributeValue[]
  } | null
  attribute_values?: HoldingAttributeValue[]
  transaction_attribute_values?: TransactionAttributeValue[]
  registration_field_values?: Record<string, unknown>
  warranty_summary?: WarrantySummary | null
  warranties?: HoldingWarranty[]
  purchase_date?: string | null
  status?: string
  pending_exception_count?: number
  pending_prominent_exception_count?: number
  workflow_states?: WorkflowStateRef[]
  created_at: string
  updated_at?: string
}

export function dedupeColumnsById<T extends { id: string }>(cols: T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const col of cols) {
    if (seen.has(col.id)) continue
    seen.add(col.id)
    out.push(col)
  }
  return out
}

/**
 * Resolves the full, config-driven Registrations table column pool.
 *
 * This is `baseColumns` from the Registrations list page, unchanged: the
 * config-injected `configuredRegistrationColumns` when present, else
 * `fallbackColumns`, plus product/purchase/warranty/consumer attribute
 * columns layered on top. Every column carries a `defaultVisible` flag so
 * consumers (the list page's sortable-columns wrapper, the dashboard tile)
 * can filter/augment without re-deriving column definitions.
 */
export function useRegistrationTableColumns(): DataTableColumn<HoldingRow>[] {
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const navigate = useNavigate()

  // Templates - for dynamic attribute columns. Holding/product attrs come
  // from product templates; purchase attrs come from transaction templates;
  // warranty attrs come from warranty templates. All four feed the columns
  // dropdown so the registration table display config only sets the default
  // visible set, not the available set.
  const { data: templates } = useProductTemplates()
  const { data: transactionTemplates } = useTransactionTemplates()
  const { data: warrantyTemplates } = useWarrantyTemplates()
  const { data: holdingEntity, isConfigLoading: holdingConfigLoading } =
    useEntityTypeByKey('holding')
  const { data: entityTypes = [] } = useEntityTypes()
  const { data: registrationSettings } = useRegistrationSettings()
  const statusWorkflowKey = useMemo(
    () => getConfiguredStatusWorkflowKey(holdingEntity?.attributes),
    [holdingEntity?.attributes],
  )

  // Partner-facing role name (e.g. "Installer" for OC, "Contractor"
  // for Supagard). Used to label the dealer/partner column and the
  // auto-generated ``installer_name`` attribute column so the table
  // speaks the tenant's vocabulary instead of a hardcoded "Dealer".
  const { data: externalRoles = [] } = useExternalRoles()
  const partnerRoleName = externalRoles[0]?.name ?? 'Dealer'

  // Active product templates - those with at least one product. Templates
  // with ``product_count === 0`` are configured but never wired into a
  // registration flow (typically left over from seed scripts or tenant
  // bootstrap). Their attributes pollute the columns dropdown without
  // ever appearing on a row, so we exclude them everywhere we surface
  // template-derived columns. When ``product_count`` is not provided
  // (older API responses), we fall back to including the template.
  const activeTemplates = useMemo(
    () =>
      (templates ?? []).filter(
        (t) => t.product_count == null || t.product_count > 0,
      ),
    [templates],
  )

  // All template-derived columns route their value resolution through
  // the same ``resolveRegistrationDisplayColumnValue`` the registration
  // detail page uses. That single resolver knows how to walk holding →
  // product / consumer / warranty / organisation, fall back across
  // ``attribute_values`` / ``registration_field_values`` / record fields,
  // and read transaction EAV values via the ``transaction_attribute:``
  // id branch - so a column added from the picker renders the same
  // value the detail card shows.
  const resolveSyntheticColumnValue = useCallback(
    (row: HoldingRow, column: EntityTableDisplayColumn) =>
      resolveRegistrationDisplayColumnValue(row as unknown as Holding, column, {
        statusWorkflowKey,
        entityTypes,
      }),
    [statusWorkflowKey, entityTypes],
  )

  // Build dynamic attribute columns from holding-level template attributes
  const attributeColumns: DataTableColumn<HoldingRow>[] = useMemo(() => {
    if (!activeTemplates.length) return []
    const seen = new Set<string>()
    const cols: DataTableColumn<HoldingRow>[] = []
    for (const tmpl of activeTemplates) {
      for (const attr of tmpl.attributes ?? []) {
        if (attr.attribute_level !== 'HOLDING') continue
        if (seen.has(attr.code_name)) continue
        // ``installer_name`` mirrors the partnership downstream org name
        // (see the wizard's InlineDealerField onSelect), which is the
        // same value the dealer/role column already renders via
        // ``row.dealer_name``. Skip the attribute to avoid a duplicate
        // "Installer" column in the picker.
        if (attr.code_name === 'installer_name') continue
        seen.add(attr.code_name)
        const syntheticColumn: EntityTableDisplayColumn = {
          id: `attr:${attr.code_name}`,
          entity_key: 'holding',
          attribute_code_name: attr.code_name,
        }
        cols.push({
          id: `attr:${attr.code_name}`,
          header: attr.display_name,
          defaultVisible: false,
          field: defineField({
            code_name: attr.code_name,
            display_name: attr.display_name,
            field_type: attr.field_type as FieldType,
            configuration_data: attr.configuration_data,
          }),
          getValue: (row) => resolveSyntheticColumnValue(row, syntheticColumn),
          meta: attr.field_type === 'SELECT' || attr.field_type === 'MULTI_SELECT'
            ? {
                filterType: 'select' as const,
                filterOptions: (attr.configuration_data?.options as Array<{ value: string; label: string }>) ?? [],
              }
            : undefined,
        })
      }
    }
    return cols
  }, [activeTemplates, resolveSyntheticColumnValue])

  // Product-level template attribute columns (attribute_level = PRODUCT
  // or PRODUCT_VARIANT). Distinct id prefix from holding attrs so a given
  // code_name can appear at both levels without colliding.
  const productAttributeColumns: DataTableColumn<HoldingRow>[] = useMemo(() => {
    if (!activeTemplates.length) return []
    const seen = new Set<string>()
    const cols: DataTableColumn<HoldingRow>[] = []
    for (const tmpl of activeTemplates) {
      for (const attr of tmpl.attributes ?? []) {
        if (attr.attribute_level === 'HOLDING') continue
        if (seen.has(attr.code_name)) continue
        seen.add(attr.code_name)
        const syntheticColumn: EntityTableDisplayColumn = {
          id: `product_attr:${attr.code_name}`,
          entity_key: 'holding',
          attribute_code_name: attr.code_name,
          relationship_path: ['product'],
        }
        cols.push({
          id: `product_attr:${attr.code_name}`,
          header: attr.display_name,
          defaultVisible: false,
          field: defineField({
            code_name: attr.code_name,
            display_name: attr.display_name,
            field_type: attr.field_type as FieldType,
            configuration_data: attr.configuration_data,
          }),
          getValue: (row) => resolveSyntheticColumnValue(row, syntheticColumn),
        })
      }
    }
    return cols
  }, [activeTemplates, resolveSyntheticColumnValue])

  // Purchase / transaction template attribute columns. Values come from
  // ``row.transaction_attribute_values`` on the holdings list response.
  // Scoped to transaction templates referenced by an active product
  // template - this excludes orphan/seed templates that the tenant has
  // configured but never wires into a registration flow.
  const purchaseAttributeColumns: DataTableColumn<HoldingRow>[] = useMemo(() => {
    if (!transactionTemplates?.length) return []
    const linkedUuids = new Set<string>()
    for (const product of activeTemplates) {
      const u = product.transaction_template?.uuid
      if (u) linkedUuids.add(u)
    }
    if (linkedUuids.size === 0) return []
    const seen = new Set<string>()
    const cols: DataTableColumn<HoldingRow>[] = []
    for (const tmpl of transactionTemplates) {
      if (!linkedUuids.has(tmpl.uuid)) continue
      for (const attr of tmpl.attributes ?? []) {
        if (seen.has(attr.code_name)) continue
        seen.add(attr.code_name)
        // ``transaction_attribute:`` id prefix triggers the resolver's
        // fast path that reads ``holding.transaction_attribute_values``
        // directly. The DataTable column id stays ``purchase_attr:`` so
        // existing saved views and column-visibility prefs keep working.
        const syntheticColumn: EntityTableDisplayColumn = {
          id: `transaction_attribute:${attr.code_name}`,
          entity_key: 'holding',
          attribute_code_name: attr.code_name,
        }
        cols.push({
          id: `purchase_attr:${attr.code_name}`,
          header: attr.display_name,
          defaultVisible: false,
          field: defineField({
            code_name: attr.code_name,
            display_name: attr.display_name,
            field_type: attr.field_type as FieldType,
            configuration_data: attr.configuration_data,
          }),
          getValue: (row) => resolveSyntheticColumnValue(row, syntheticColumn),
        })
      }
    }
    return cols
  }, [transactionTemplates, activeTemplates, resolveSyntheticColumnValue])

  // Warranty template attribute columns. A holding can carry multiple
  // warranties; we read from the first one.
  const warrantyAttributeColumns: DataTableColumn<HoldingRow>[] = useMemo(() => {
    if (!warrantyTemplates?.length) return []
    const seen = new Set<string>()
    const cols: DataTableColumn<HoldingRow>[] = []
    for (const tmpl of warrantyTemplates) {
      for (const attr of tmpl.attributes ?? []) {
        if (seen.has(attr.code_name)) continue
        seen.add(attr.code_name)
        const syntheticColumn: EntityTableDisplayColumn = {
          id: `warranty_attr:${attr.code_name}`,
          entity_key: 'holding',
          attribute_code_name: attr.code_name,
          relationship_path: ['warranty'],
        }
        cols.push({
          id: `warranty_attr:${attr.code_name}`,
          header: attr.display_name,
          defaultVisible: false,
          field: defineField({
            code_name: attr.code_name,
            display_name: attr.display_name,
            field_type: attr.field_type as FieldType,
            configuration_data: attr.configuration_data,
          }),
          getValue: (row) => resolveSyntheticColumnValue(row, syntheticColumn),
        })
      }
    }
    return cols
  }, [warrantyTemplates, resolveSyntheticColumnValue])

  // Consumer entity EAV attribute columns. The consumer entity's
  // attributes (email, first_name, last_name, phone_number are COLUMN,
  // address + any custom fields are EAV) are discoverable from the
  // entity registry. We skip COLUMN attributes that already have
  // dedicated fallback columns (consumer name, email, phone) and
  // surface only the EAV attributes (address, custom fields) so they
  // appear in the column picker for all tenants generically.
  const consumerEntity = useMemo(
    () => entityTypes.find((e) => e.key === 'consumer'),
    [entityTypes],
  )

  const consumerAttributeColumns: DataTableColumn<HoldingRow>[] = useMemo(() => {
    if (!consumerEntity?.attributes?.length) return []
    // Core COLUMN fields already covered by fallback columns
    const SKIP_CORE = new Set(['email', 'first_name', 'last_name', 'phone_number'])
    const cols: DataTableColumn<HoldingRow>[] = []
    for (const attr of consumerEntity.attributes) {
      if (SKIP_CORE.has(attr.code_name)) continue
      const syntheticColumn: EntityTableDisplayColumn = {
        id: `consumer_attr:${attr.code_name}`,
        entity_key: 'consumer',
        attribute_code_name: attr.code_name,
        relationship_path: ['consumer'],
      }
      cols.push({
        id: `consumer_attr:${attr.code_name}`,
        header: attr.display_name,
        defaultVisible: false,
        field: defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: attr.field_type as FieldType,
          configuration_data: attr.configuration_data,
        }),
        getValue: (row) => resolveSyntheticColumnValue(row, syntheticColumn),
      })
    }
    return cols
  }, [consumerEntity, resolveSyntheticColumnValue])

  const entityWorkflowColumns: DataTableColumn<HoldingRow>[] = useMemo(
    () =>
      entityDisplayAttributes(holdingEntity?.attributes, 'table').map((attr) => ({
        id: `entity:${attr.code_name}`,
        header: attr.configuration_data.display?.label || attr.display_name,
        defaultVisible: attr.configuration_data.display?.table ?? false,
        cell: (row) => {
          if (attr.configuration_data.value_source === 'entity_relationship') {
            const value = formatRelationshipValue(
              row,
              attr.configuration_data.relationship_key || attr.code_name,
            )
            return value || <span className="text-[11px] text-[var(--ink-3)]">{'-'}</span>
          }
          const workflowKey = attr.configuration_data.workflow_key || attr.code_name
          const wf = pickWorkflowState(row.workflow_states, workflowKey)
          if (!wf) {
            return <span className="text-[11px] text-[var(--ink-3)]">{'-'}</span>
          }
          return (
            <WorkflowStateBadge
              workflowKey={wf.workflow_key}
              stateKey={wf.state_key}
              label={wf.state_label || wf.state_key}
              color={wf.state_color}
              isTerminal={wf.is_terminal}
            />
          )
        },
      })),
    [holdingEntity?.attributes],
  )

  const sourceDisplayColumns: EntityTableDisplayColumn[] = useMemo(() => {
    const holdingColumns =
      holdingEntity?.metadata?.display?.registration_table?.columns
    const hostColumns = entityTypes.find((e) => e.key === 'host')?.metadata
      ?.display?.registration_table?.columns
    return holdingColumns && holdingColumns.length > 0
      ? holdingColumns
      : (hostColumns ?? [])
  }, [entityTypes, holdingEntity])

  const configuredRegistrationColumns: DataTableColumn<HoldingRow>[] = useMemo(
    () => {
      return sourceDisplayColumns.map((column) => {
          // Look up the terminal entity attribute up front: it drives both
          // the workflow-state dispatch below and DataTable's value
          // formatting (dates, money, selects, booleans, etc.). When the
          // attribute is not in the registry - e.g. derived columns like
          // host.address - DataTable falls back to rendering the resolver's
          // already-stringified output, which is fine for plain text.
          const terminalEntityKey = resolveTerminalEntityKey(column, entityTypes)
          const attribute = entityTypes
            .find((e) => e.key === terminalEntityKey)
            ?.attributes.find(
              (a) => a.code_name === column.attribute_code_name,
            )

          // Workflow-state columns (Payment -> lifecycle_payment, Status ->
          // lifecycle_status) resolve from the holding's workflow_states via
          // the generic resolver. The lifecycle status column keeps its
          // StatusBadge (plain label + draft-alert affordance); every other
          // workflow column renders a coloured WorkflowStateBadge. These
          // attributes carry ``display.table = false`` so they never also
          // render via ``entityWorkflowColumns`` (the ad-hoc "+ Add column"
          // path) - avoiding a duplicate column.
          if (attribute?.configuration_data?.value_source === 'workflow_state') {
            const attrWorkflowKey =
              attribute.configuration_data.workflow_key ||
              column.attribute_code_name
            const isLifecycleStatus = attrWorkflowKey === statusWorkflowKey
            return {
              id: column.id,
              header: column.label || attribute.display_name,
              // Config-injected Payment/Status columns carry
              // ``default_visible: false`` so they stay available-but-hidden
              // in the column picker, preserving the prior hardcoded-fallback
              // appearance (no visual change on migration).
              defaultVisible: column.default_visible ?? true,
              cell: (row: HoldingRow) => {
                const value = resolveRegistrationColumnValue(
                  row,
                  column,
                  statusWorkflowKey,
                  entityTypes,
                )
                if (isLifecycleStatus) {
                  const showDraftAlert =
                    pickWorkflowState(row.workflow_states, statusWorkflowKey)
                      ?.state_key === 'draft' &&
                    (row.pending_prominent_exception_count ?? 0) > 0
                  return (
                    <StatusBadge
                      status={String(value || '-')}
                      icon={
                        showDraftAlert ? (
                          <AlertTriangle className="h-3 w-3 text-[var(--ink-3)]" />
                        ) : undefined
                      }
                      tooltip={
                        showDraftAlert
                          ? 'Approval required before this registration can go live.'
                          : undefined
                      }
                    />
                  )
                }
                if (isWorkflowStateResolution(value)) {
                  return (
                    <WorkflowStateBadge
                      workflowKey={value.workflowKey}
                      stateKey={value.stateKey}
                      label={value.label}
                      color={value.color}
                      isTerminal={value.isTerminal}
                    />
                  )
                }
                return (
                  <span className="text-[11px] text-[var(--ink-3)]">{'-'}</span>
                )
              },
            }
          }

          const field = attribute
            ? defineField({
                code_name: attribute.code_name,
                display_name: attribute.display_name,
                field_type: attribute.field_type as FieldType,
                configuration_data: attribute.configuration_data,
              })
            : undefined

          return {
            id: column.id,
            header: column.label || column.attribute_code_name,
            defaultVisible: column.default_visible ?? true,
            field,
            getValue: (row: HoldingRow) =>
              resolveRegistrationColumnValue(
                row,
                column,
                statusWorkflowKey,
                entityTypes,
              ),
          }
        })
    },
    [entityTypes, sourceDisplayColumns, statusWorkflowKey],
  )

  const registrationIdColumn: DataTableColumn<HoldingRow> = useMemo(
    () => ({
      id: 'ref',
      header: 'Reg #',
      cell: (row) => (
        <span
          className="truncate font-mono text-[12px] uppercase text-[var(--ink-3)]"
        >
          {getRegistrationNumber(row)}
        </span>
      ),
    }),
    [],
  )

  // Platform column, shown for every tenant whatever its configured columns,
  // right after the registration ID so it is in view without scrolling.
  const lastActivityColumn: DataTableColumn<HoldingRow> = useMemo(
    () => ({
      id: 'updated_at',
      header: t('common.lastActivity', 'Last Activity'),
      sortable: true,
      sortField: 'updated_at',
      sortKind: 'date',
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink-2)]">
          {row.updated_at ? formatDate(row.updated_at) : '-'}
        </span>
      ),
    }),
    [t, formatDate],
  )

  // Host-based tenants (property, vehicle) identify a registration by where
  // it is, so the host's address sits next to the Reg # on every surface
  // that lists registrations - searching by address is useless when the
  // results only show a reference number. Skipped when the tenant already
  // configured a host address column of their own, and when registrations
  // have no host at all (`standard` tenants).
  const hostAddressColumn: DataTableColumn<HoldingRow> | null = useMemo(() => {
    if (!isHostBasedDetailView(registrationSettings?.registration_detail_view)) {
      return null
    }
    // Only step aside for a tenant-authored host address column once the
    // holding config has actually landed. Until then `sourceDisplayColumns`
    // falls back to the HOST entity's columns, which for a tenant whose real
    // config lives on the holding is a different table entirely - and reading
    // that transient value suppressed this column on first paint and left it
    // suppressed in whatever the preference layer captured (SX-229's flash,
    // one layer down).
    const alreadyConfigured =
      !holdingConfigLoading &&
      sourceDisplayColumns.some(
        (column) =>
          resolveTerminalEntityKey(column, entityTypes) === 'host' &&
          column.attribute_code_name?.startsWith('address'),
      )
    if (alreadyConfigured) return null
    const hostEntity = entityTypes.find((e) => e.key === 'host')
    if (!hostCarriesAddress(hostEntity)) return null
    // The host entity's own label names it ("Property", "Vehicle"), so the
    // header follows the tenant's registry the way every other column in this
    // table does. A tenant whose host is a Site or a Vessel is then named
    // correctly with no code change. Falls back to the detail-view noun while
    // the registry is still loading.
    const hostLabel =
      hostEntity.label?.trim() ||
      capitalizeFirstLetter(
        hostNounForDetailView(registrationSettings?.registration_detail_view)
          .singular,
      )
    return {
      id: HOST_ADDRESS_DISPLAY_COLUMN.id,
      header: t('registrations.columnHostAddress', '{{hostNoun}} Address', {
        hostNoun: hostLabel,
      }),
      defaultVisible: true,
      getValue: (row: HoldingRow) =>
        resolveRegistrationColumnValue(
          row,
          HOST_ADDRESS_DISPLAY_COLUMN,
          statusWorkflowKey,
          entityTypes,
        ),
    }
  }, [
    entityTypes,
    holdingConfigLoading,
    registrationSettings?.registration_detail_view,
    sourceDisplayColumns,
    statusWorkflowKey,
    t,
  ])

  const fallbackColumns: DataTableColumn<HoldingRow>[] = useMemo(
    () => [
      registrationIdColumn,
      lastActivityColumn,
      ...(hostAddressColumn ? [hostAddressColumn] : []),
      {
        id: 'registration_date',
        header: 'Registration Date',
        sortable: true,
        sortField: 'created_at',
        sortKind: 'date',
        cell: (row) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {row.created_at ? formatDate(row.created_at) : '-'}
          </span>
        ),
      },
      {
        id: 'product',
        header: t('registrations.columnProduct'),
        // Default-visible: tenants with SKU baked into ``cached_short_name``
        // (e.g. "QC LED LIGHT (SKU: AQK-30-91175)") see model + SKU at a
        // glance without toggling the column on. Was hidden by default;
        // users expected to see the SKU inline, consistent with the
        // Library list.
        defaultVisible: true,
        sortable: true,
        sortField: 'product__cached_short_name',
        sortKind: 'text',
        cell: (row) => (
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/registrations/${row.uuid}`)
            }}
            className="block max-w-[320px] truncate cursor-pointer text-[13px] text-[var(--ink)] transition-opacity hover:opacity-70"
          >
            {row.product?.cached_short_name || t('registrations.unknownProduct')}
          </button>
        ),
      },
      {
        id: 'consumer',
        header: t('registrations.columnConsumer'),
        defaultVisible: false,
        sortable: true,
        sortField: 'consumer_account__first_name',
        sortKind: 'text',
        meta: { filterType: 'text' as const, filterField: 'search' },
        cell: (row) =>
          row.consumer_account ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/registrations/${row.uuid}`)
              }}
              className="block truncate cursor-pointer text-[13px] text-[var(--ink-2)] transition-opacity hover:opacity-70"
            >
              {`${row.consumer_account.first_name} ${row.consumer_account.last_name}`.trim() ||
                row.consumer_account.email ||
                '-'}
            </button>
          ) : (
            '-'
          ),
      },
      {
        id: 'email',
        header: t('registrations.columnEmail'),
        defaultVisible: false,
        sortable: true,
        sortField: 'consumer_account__email',
        sortKind: 'text',
        cell: (row) => (
          <span className="block truncate text-[12px] text-[var(--ink-3)]">
            {row.consumer_account?.email || '-'}
          </span>
        ),
      },
      {
        id: 'registered_by',
        header: t('registrations.registeredBy', 'Registered by'),
        defaultVisible: false,
        cell: (row) => (
          <span className="block truncate text-[12px] text-[var(--ink-3)]">
            {row.registered_by_email || '-'}
          </span>
        ),
      },
      {
        id: 'phone',
        header: 'Phone',
        defaultVisible: false,
        cell: (row) => (
          <span className="block truncate text-[12px] text-[var(--ink-3)]">
            {row.consumer_phone || '-'}
          </span>
        ),
      },
      {
        id: 'dealer',
        header: partnerRoleName,
        defaultVisible: false,
        sortable: true,
        sortField: 'organisation__name',
        sortKind: 'text',
        cell: (row) => (
          <span className="block truncate text-[12px] text-[var(--ink-2)]">
            {row.dealer_name || '-'}
          </span>
        ),
      },
      {
        id: 'warranty',
        header: 'Warranty',
        defaultVisible: false,
        cell: (row) => {
          const w = row.warranty_summary
          if (!w) return '-'
          return (
            <span className="block truncate text-[12px] text-[var(--ink-2)]">
              {w.display_name}
              {w.expires_at && (
                <span className="ml-1 text-[var(--ink-3)]">
                  ({formatDate(w.expires_at)})
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'created_at',
        header: t('registrations.columnRegistered'),
        defaultVisible: false,
        sortable: true,
        sortField: 'created_at',
        sortKind: 'date',
        cell: (row) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {formatDate(row.created_at)}
          </span>
        ),
      },
      ...entityWorkflowColumns,
      ...attributeColumns,
    ],
    [
      t,
      navigate,
      entityWorkflowColumns,
      attributeColumns,
      partnerRoleName,
      registrationIdColumn,
      hostAddressColumn,
      lastActivityColumn,
      formatDate,
    ],
  )
  const baseColumns = useMemo(() => {
    const pool: DataTableColumn<HoldingRow>[] = [
      ...fallbackColumns,
      ...productAttributeColumns,
      ...purchaseAttributeColumns,
      ...warrantyAttributeColumns,
      ...consumerAttributeColumns,
    ]
    if (configuredRegistrationColumns.length === 0) {
      return dedupeColumnsById(pool)
    }
    const configuredIds = new Set(configuredRegistrationColumns.map((c) => c.id))
    const others = pool
      .filter(
        (c) =>
          c.id !== 'ref' &&
          c.id !== hostAddressColumn?.id &&
          c.id !== lastActivityColumn.id &&
          !configuredIds.has(c.id),
      )
      .map((c) => ({ ...c, defaultVisible: false as const }))
    return dedupeColumnsById([
      registrationIdColumn,
      lastActivityColumn,
      ...(hostAddressColumn ? [hostAddressColumn] : []),
      ...configuredRegistrationColumns.map((c) => ({
        ...c,
        defaultVisible: c.defaultVisible ?? true,
      })),
      ...others,
    ])
  }, [
    configuredRegistrationColumns,
    fallbackColumns,
    registrationIdColumn,
    hostAddressColumn,
    lastActivityColumn,
    productAttributeColumns,
    purchaseAttributeColumns,
    warrantyAttributeColumns,
    consumerAttributeColumns,
  ])

  return baseColumns
}
